import React, { useState, useEffect } from "react";
import { Typography, Box, Button, Snackbar, Alert } from "@mui/material";
import api from "../../../services/api";
import IngredientTable from "./IngredientTable";
import IngredientFormDrawer from "./IngredientFormDrawer";
import { useSnackbarAlert } from "../../../hooks/useSnackbarAlert";
import AddIcon from "@mui/icons-material/Add";
import { useAppConfig } from "../../../context/AppConfigContext";

const AdminIngredientsPage = () => {
  const [ingredients, setIngredients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    internalValue: "",
    category: "Other",
    unit: "g",
    costPerUnit: 0,
    supplier: "",
    unitAmount: "",
    storePathIndex: 999,
  });
  const snackbar = useSnackbarAlert();
  const { config } = useAppConfig();
  const units = config?.ingredients?.units || [];
  const categories = config?.ingredients?.categories || [];
  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "asc",
  });

  const fetchIngredients = async () => {
    const res = await api.get("/admin/ingredients");
    setIngredients(res.data);
  };

  const fetchSuppliers = async () => {
    const res = await api.get("/admin/suppliers");
    setSuppliers(res.data);
  };

  useEffect(() => {
    fetchIngredients();
    fetchSuppliers();
  }, []);

  const handleSubmit = async () => {
    try {
      if (editing) {
        await api.put(`/admin/ingredients/${editing._id}`, formData);
        snackbar.success("Ingredient updated");
      } else {
        await api.post("/admin/ingredients", formData);
        snackbar.success("Ingredient created");
      }
      setOpen(false);
      setEditing(null);
      resetForm();
      fetchIngredients();
    } catch (err) {
      snackbar.error(err.message);
    }
  };

  const handleDelete = async (id) => {
    await api.delete(`/admin/ingredients/${id}`);
    fetchIngredients();
    snackbar.info("Ingredient deleted");
  };

  const resetForm = () => {
    setFormData({
      name: "",
      internalValue: "",
      category: "Other",
      unit: "g",
      costPerUnit: 0,
      supplier: "",
      unitAmount: "",
      priceChangeReason: "",
      storePathIndex: 999,
    });
    setEditing(null);
    setOpen(false);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}>
        <Typography variant="h5">Manage Ingredients</Typography>

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}>
          Add Ingredient
        </Button>
      </Box>

      <IngredientTable
        ingredients={ingredients}
        suppliers={suppliers}
        onEdit={(ingredient) => {
          setEditing(ingredient);
          setFormData({
            name: ingredient.name,
            internalValue: ingredient.internalValue || "",
            category: ingredient.category,
            unit: ingredient.unit,
            costPerUnit: ingredient.costPerUnit,
            supplier: ingredient.supplier,
            unitAmount: ingredient.unitAmount,
            storePathIndex: ingredient.storePathIndex ?? 999,
          });
          setOpen(true);
        }}
        onDelete={handleDelete}
        sortConfig={sortConfig}
        setSortConfig={setSortConfig}
      />

      <IngredientFormDrawer
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        editing={editing}
        categories={categories}
        units={units}
        suppliers={suppliers}
      />
    </Box>
  );
};

export default AdminIngredientsPage;
